import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import * as inventoryApi from '../services/inventoryApi'
import { useAuth } from './AuthProvider.jsx'

const InventoryContext = createContext(null)

export function InventoryProvider({ children }) {
  const { isAuthenticated, authReady } = useAuth()
  const [parts, setParts] = useState([])
  const [transactions, setTransactions] = useState([])
  const [summary, setSummary] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  const refresh = useCallback(async () => {
    if (!isAuthenticated) return
    setLoading(true)
    setError('')
    try {
      const [nextParts, nextTx, nextSummary] = await Promise.all([
        inventoryApi.fetchParts(),
        inventoryApi.fetchTransactions({ limit: 500 }),
        inventoryApi.fetchDashboardSummary(),
      ])
      setParts(nextParts)
      setTransactions(nextTx)
      setSummary(nextSummary)
    } catch (err) {
      setError(err.message || '데이터를 불러오는 중 오류가 발생했습니다.')
    } finally {
      setLoading(false)
    }
  }, [isAuthenticated])

  useEffect(() => {
    if (!authReady || !isAuthenticated) return undefined
    refresh()
    return undefined
  }, [authReady, isAuthenticated, refresh])

  const value = useMemo(
    () => ({
      parts,
      transactions,
      summary,
      loading,
      error,
      refresh,
      getPart(id) {
        return parts.find((part) => part.id === id) || null
      },
      async loadPart(id) {
        const found = parts.find((part) => part.id === id)
        if (found) return found
        const part = await inventoryApi.fetchPart(id)
        if (part) {
          setParts((current) => (current.some((item) => item.id === part.id) ? current : [...current, part]))
        }
        return part
      },
      async fetchPartHistory(partId) {
        return inventoryApi.fetchTransactions({ partId, limit: 200 })
      },
      async createPart(form) {
        const result = await inventoryApi.createPart(inventoryApi.toPartPayload(form, { includeQuantity: true }))
        await refresh()
        return result.part
      },
      async updatePart(id, form) {
        const part = await inventoryApi.updatePart(id, inventoryApi.toPartPayload(form, { includeQuantity: false }))
        await refresh()
        return part
      },
      async stockIn(payload) {
        const result = await inventoryApi.stockIn(payload)
        await refresh()
        return result
      },
      async stockOut(payload) {
        const result = await inventoryApi.stockOut(payload)
        await refresh()
        return result
      },
      async stockAdjust(payload) {
        const result = await inventoryApi.stockAdjust(payload)
        await refresh()
        return result
      },
      async reviewPart(id, payload) {
        const result = await inventoryApi.reviewPart(id, payload)
        await refresh()
        return result
      },
      async mergePart(sourceId, targetPartId) {
        const result = await inventoryApi.mergePart(sourceId, targetPartId)
        await refresh()
        return result
      },
      async deletePart(id) {
        const result = await inventoryApi.deletePart(id)
        setParts((current) => current.filter((part) => part.id !== id))
        await refresh()
        return result
      },
    }),
    [parts, transactions, summary, loading, error, refresh],
  )

  return <InventoryContext.Provider value={value}>{children}</InventoryContext.Provider>
}

export function useInventory() {
  const ctx = useContext(InventoryContext)
  if (!ctx) throw new Error('useInventory는 InventoryProvider 안에서만 사용할 수 있습니다.')
  return ctx
}
